import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { useCallback } from "react";
import { databasePromise, getAuthAsync } from "configuration";

/**
 * Returns stable like/unlike callbacks for a given Firestore collection.
 * Handles auth check and arrayUnion/arrayRemove — callers get two functions.
 *
 * Usage:
 *   const { like: likeDeal, unlike: unlikeDeal } = useLike("deals");
 *   const { like: likeRestaurant, unlike: unlikeRestaurant } = useLike("restaurants");
 */
export const useLike = (collectionName) => {
  const like = useCallback(
    async (id) => {
      try {
        const auth = await getAuthAsync();
        if (!auth.currentUser)
          throw new Error(`User must be signed in to like a ${collectionName}`);
        const db = await databasePromise;
        await updateDoc(doc(db, collectionName, id), {
          likedBy: arrayUnion(auth.currentUser.uid),
        });
      } catch (error) {
        console.error(`Error liking ${collectionName}:`, error);
      }
    },
    [collectionName],
  );

  const unlike = useCallback(
    async (id) => {
      try {
        const auth = await getAuthAsync();
        if (!auth.currentUser)
          throw new Error(
            `User must be signed in to unlike a ${collectionName}`,
          );
        const db = await databasePromise;
        await updateDoc(doc(db, collectionName, id), {
          likedBy: arrayRemove(auth.currentUser.uid),
        });
      } catch (error) {
        console.error(`Error unliking ${collectionName}:`, error);
      }
    },
    [collectionName],
  );

  return { like, unlike };
};